import { useCallback, useEffect, useRef, useState } from 'react';

import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { nanoid } from '@reduxjs/toolkit';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { Listener } from '@schibsted-svp/web-player';
import type { Location } from 'history';

import { fetchSvpAssetById } from '../../modules/assets/actions';
import { observeViewportResize } from '../../utils/viewport/observeViewportResize';
import { playerContainer } from '../../player/playerContainer';
import { isMobile } from '../../utils/device';
import { observeWindowScroll } from '../../utils/observeWindowScroll';
import { usePlayerContext } from '../../player/PlayerContext';
import { getAssetAspectRatio } from '../../shared/helpers/assetHelpers';
import { usePlayer } from '../../player/hooks/usePlayer';
import { OrientationType } from '../../utils/screenOrientation';
import { normalizeAspectRatio } from '../../player/playerAssetHelpers';
import { config } from '../../config';
import { useAppSelector } from '../../modules/hooks';
import { selectCurrentlyPlayingAsset } from '../../modules/ui';
import type { Vendor } from '../../types';
import { usePipContext } from '../../player/PictureInPictureProvider';
import { getAssetPagePath } from '../../shared/helpers/getAssetPagePath';
import { getAssetIdFromLocation } from '../../helpers/getAssetIdFromLocation';
import { getInitialNextAsset } from '../../player/playerService';

import { useDeviceOrientation } from './useDeviceOrientation';
import { usePlayerEvent } from './usePlayerEvent';
import type { AdStartedParam } from './usePlayerEvent';

export const isMatchingPath = ({
  location,
  vendor,
  assetId,
}: {
  location: Location;
  vendor: Vendor;
  assetId: number;
}) => {
  return getAssetIdFromLocation({ location, vendor }) === assetId;
};

export const getPlayNextAssetId = (assetId: unknown) => {
  // @ts-ignore
  let id = parseInt(assetId, 10);

  if (isNaN(id) && assetId && (assetId as SvpAssetAdditional).id) {
    // @ts-ignore
    id = parseInt((assetId as SvpAssetAdditional).id);
  }

  return id;
};

/**
 * This hook is responsible for binding to global {@link SvpPlayer} events and
 * connecting it to routing, redux store, non-react HTML, etc.
 */
function usePlayerBindings() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { isPlaying, isCasting } = usePlayerContext();
  const isPipEnabled = usePipContext();
  const orientation = useDeviceOrientation();
  const player = usePlayer();
  const currentlyPlayingAsset = useAppSelector(selectCurrentlyPlayingAsset);
  const currentlyPlayingAssetRef = useRef(currentlyPlayingAsset);
  // when the button is clicked disable affixing until user scrolls back to the top
  const [affixPrevented, preventAffix] = useState(false);

  currentlyPlayingAssetRef.current = currentlyPlayingAsset;

  /**
   * Binds to autoplay next and recommended grid events, ensuring that assets are
   * available in the redux store before playback, and updating the current location
   * when playback occurs.
   *
   * @type {function(SvpPlayer)}
   */
  useEffect(() => {
    if (!player) {
      return;
    }

    const isMatchingCurrentAssetPath = () => {
      const currentlyPlayingAsset = currentlyPlayingAssetRef.current;

      if (!currentlyPlayingAsset) {
        return true;
      }

      return isMatchingPath({
        location: history.location,
        assetId: currentlyPlayingAssetRef.current?.id!,
        vendor: currentlyPlayingAssetRef.current?.provider ?? config.provider,
      });
    };

    function onPlayNext(assetId: number | SvpAssetAdditional) {
      if ((isCasting || isPipEnabled) && !isMatchingCurrentAssetPath()) {
        return;
      }

      const id = getPlayNextAssetId(assetId);

      const vendor =
        typeof assetId === 'number'
          ? (currentlyPlayingAssetRef.current?.provider ?? config.provider)
          : assetId.provider;

      if (
        id &&
        !isMatchingPath({
          location: history.location,
          assetId: id,
          vendor,
        })
      ) {
        // update the location when an asset is started automatically or from the recommended grid
        history.push(getAssetPagePath({ assetId: id, vendor }));
      }
    }

    function onNextAssetReady(asset: SvpAssetAdditional) {
      // ensure that our store contains the asset which might automatically play next
      dispatch(
        fetchSvpAssetById.fulfilled(asset, nanoid(), {
          assetId: asset.id,
          vendor: asset.provider,
        })
      );
    }

    const nextAsset = getInitialNextAsset();
    if (nextAsset) {
      onNextAssetReady(nextAsset);
    }

    player.on('playNext', onPlayNext);
    player.on('VgtvSkinPlugin:nextAssetReady', onNextAssetReady as Listener);

    return () => {
      player.off('playNext', onPlayNext);
      player.off('VgtvSkinPlugin:nextAssetReady', onNextAssetReady as Listener);
    };
  }, [isCasting, isPipEnabled, dispatch, history, player]);

  const deriveAspectRatioFromAsset = useCallback(() => {
    if (player) {
      playerContainer.setAspectRatio(getAssetAspectRatio(player.getRawAsset()));
    }
  }, [player]);

  const deriveAspectRatioFromAd = useCallback((data: AdStartedParam) => {
    playerContainer.setAspectRatio(normalizeAspectRatio(data.aspectRatio));
  }, []);

  usePlayerEvent('play', deriveAspectRatioFromAsset);
  usePlayerEvent('adStarted', deriveAspectRatioFromAd);

  const updateAffix = useCallback(() => {
    const scrollTop = window.scrollY;
    const headerHeight = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--header-height'
      )
    );

    if (scrollTop >= headerHeight) {
      if (
        isPlaying &&
        !affixPrevented &&
        orientation === OrientationType.PORTRAIT
      ) {
        playerContainer.affix(true);
      }
    } else {
      playerContainer.affix(false);
    }

    if (affixPrevented && scrollTop < headerHeight) {
      preventAffix(false);
    }
  }, [affixPrevented, isPlaying, orientation]);

  /**
   * Binds to window scroll events, and affixes the player to viewport top when scrolled down.
   */
  useEffect(() => {
    if (!isMobile) {
      return;
    }

    document
      .querySelector('.affix-close-button')!
      .addEventListener('click', () => {
        playerContainer.affix(false);
        preventAffix(true);
      });

    updateAffix();

    const { stopObserving } = observeWindowScroll(updateAffix, true);

    return stopObserving;
  }, [updateAffix]);

  /**
   * Binds to window resize and repositions the player above the current player component node.
   */
  useEffect(() => {
    const { stopObserving } = observeViewportResize(() => {
      playerContainer.positionOverNode();
    });

    return stopObserving;
  }, []);

  /**
   * Waits until the layout is recalculated and updates the player position.
   */
  useEffect(() => {
    setTimeout(playerContainer.positionOverNode, 20);
  }, [orientation]);
}

export { usePlayerBindings };
